import axios from 'axios';

export function readMcpTransaction(query) {
  const value = query?.mcp_txn;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function appendMcpTransaction(url, mcpTransaction) {
  if (!mcpTransaction || typeof url !== 'string' || url.length === 0) {
    return url;
  }
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('mcp_txn', mcpTransaction);
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}mcp_txn=${encodeURIComponent(mcpTransaction)}`;
  }
}

/**
 * Mint Neutron handoff target=mcp and redirect to MCP Helios callback.
 * @returns {Promise<boolean>} true when navigation started
 */
export async function completeMcpPortalLogin({
  mcpTransaction,
  mcpUrl,
  accessToken = null,
  http = axios
}) {
  if (!mcpTransaction || !mcpUrl) {
    return false;
  }

  const token =
    accessToken ||
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('access_token') : null) ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);

  if (!token) {
    console.info('Cannot complete MCP login: missing access token');
    return false;
  }

  let code;
  try {
    const response = await http.post(
      'auth/handoff-code',
      { target: 'mcp', mcp_txn: mcpTransaction },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    code = response?.data?.code;
  } catch (error) {
    console.error('Failed to mint MCP handoff code', {
      status: error?.response?.status,
      message: error?.message
    });
    return false;
  }

  if (typeof code !== 'string' || code.length === 0) {
    console.error('MCP handoff mint returned no code');
    return false;
  }

  try {
    const callback = new URL('/authorize/callback/helios', mcpUrl);
    callback.searchParams.set('mcp_txn', mcpTransaction);
    callback.searchParams.set('handoff', code);
    window.location.replace(callback.toString());
    return true;
  } catch (error) {
    console.error('Invalid MCP URL for callback', { message: error?.message });
    return false;
  }
}

export function readMcpTransactionFromLocation() {
  if (typeof window === 'undefined' || !window.location?.search) {
    return null;
  }
  return new URLSearchParams(window.location.search).get('mcp_txn');
}

/**
 * Merge extras with mcp_txn from the route query (destination), never window.location.
 * Guards run before history updates; reading location would reuse the route being left.
 * @param {Record<string, unknown>} [query]
 * @param {Record<string, unknown>} [extras]
 * @returns {Record<string, unknown>}
 */
export function buildLoginQueryWithMcpTransaction(query = {}, extras = {}) {
  const loginQuery = { ...extras };
  const mcpTransaction = readMcpTransaction(query);
  if (mcpTransaction) {
    loginQuery.mcp_txn = mcpTransaction;
  }
  return loginQuery;
}

/**
 * Keep mcp_txn on an in-app fallback route so a failed mint can retry later.
 * @param {Record<string, unknown> | null | undefined} route
 * @param {string | null | undefined} mcpTransaction
 * @returns {Record<string, unknown> | null | undefined}
 */
export function withMcpTransactionQuery(route, mcpTransaction) {
  if (!mcpTransaction || route === null || typeof route !== 'object') {
    return route;
  }
  return {
    ...route,
    query: {
      ...(route.query && typeof route.query === 'object' ? route.query : {}),
      mcp_txn: mcpTransaction
    }
  };
}
